import { useCallback } from "react";
import type { FallbackProps } from "react-error-boundary";
import {
  ActionButton,
  ActionButtonIntent,
  Icons,
  PanelOrientation,
  PanelPosition,
  ResizableTabPanels,
  Spacer,
  VStack,
} from "widgets";

import { InspectionPanelTabs, StatusPanelTabs } from "../../types.ts";
import { FetchError } from "../../utils/errors.ts";
import { ShortcutsCard } from "../help/Shortcuts.tsx";
import { GenomeViewerStatusBarActions } from "../toolbar/GenomeViewerStatusBarActions.tsx";
import { GenomeViewerToolbarContainer, ToggleInspectionPanel } from "../toolbar/GenomeViewerToolbar.tsx";
import { BadRequestPage } from "./bad-request-page.tsx";
import { ErrorPage } from "./error-page.tsx";
import { NotFoundPage } from "./not-found-page.tsx";

const ERROR_COMPONENTS = {
  400: BadRequestPage, // Validation exceptions (`403` - Forbidden - is part of them)
  404: NotFoundPage,
  default: ErrorPage,
} as const;

export const ErrorFallbackPage = ({ resetErrorBoundary, error }: FallbackProps) => {
  const getErrorComponent = useCallback(() => {
    if (!(error instanceof FetchError)) {
      return ERROR_COMPONENTS.default;
    }

    return ERROR_COMPONENTS[error.statusCode as keyof typeof ERROR_COMPONENTS] ?? ERROR_COMPONENTS.default;
  }, [error]);

  const ErrorComponent = getErrorComponent();

  return (
    <ResizableTabPanels
      orientation={PanelOrientation.Horizontal}
      fixedPanel={PanelPosition.Trailing}
      defaultSize={350}
      minSize={240}
      maxSize={480}
      isContinuous
      style={{ width: "100%", height: "100%" }}
    >
      <ResizableTabPanels.FlexPanel>
        <VStack spacing={0} style={{ width: "100%", height: "100%" }}>
          <GenomeViewerToolbarContainer>
            <Spacer />
            <ToggleInspectionPanel />
          </GenomeViewerToolbarContainer>
          <ResizableTabPanels
            orientation={PanelOrientation.Vertical}
            fixedPanel={PanelPosition.Trailing}
            minSize={250}
            defaultSize={250}
            closedSize={39}
            isContinuous
            openOnTabPress
            tabsActions={[<GenomeViewerStatusBarActions key="status-bar-toggle" />]}
            defaultOpen={false}
            style={{ width: "100%", height: "100%" }}
          >
            <ResizableTabPanels.FlexPanel>
              <VStack spacing={10} style={{ justifyContent: "center", alignItems: "center", margin: "auto" }}>
                <ErrorComponent message={error.message} />
                <ActionButton
                  onPress={() => {
                    resetErrorBoundary();
                  }}
                  label="Reset"
                  icon="restart_alt"
                  intent={ActionButtonIntent.Danger}
                />
              </VStack>
            </ResizableTabPanels.FlexPanel>
            <ResizableTabPanels.FixedPanel>
              <ResizableTabPanels.FixedPanelTab
                id={StatusPanelTabs.Help}
                title={StatusPanelTabs.Help}
                position={0}
                icon="keyboard"
              >
                <ShortcutsCard />
              </ResizableTabPanels.FixedPanelTab>
            </ResizableTabPanels.FixedPanel>
          </ResizableTabPanels>
        </VStack>
      </ResizableTabPanels.FlexPanel>
      <ResizableTabPanels.FixedPanel>
        <ResizableTabPanels.FixedPanelTab
          icon={<img src={Icons.Action.GenomeViewer} width={20} height={20} alt="" />}
          title="Synteny"
          position={0}
          id={InspectionPanelTabs.GenomeViewer}
        >
          <div />
        </ResizableTabPanels.FixedPanelTab>
        <ResizableTabPanels.FixedPanelTab icon="search" title="Search" position={1} id={InspectionPanelTabs.Search}>
          <div />
        </ResizableTabPanels.FixedPanelTab>
      </ResizableTabPanels.FixedPanel>
    </ResizableTabPanels>
  );
};
